import * as joint from '@joint/core';
import { IShapeColorable, IFillColorable, ITextColorable, IBorderColorable, Rect, CalloutPoint, CalloutSegment } from './types';
import { IconShapeDefaults, fetchIconShape } from './icons';

export class IconElement extends joint.dia.Element implements IShapeColorable {
    private static readonly DefaultSize = { width: 48, height: 48 };
    private static readonly DefaultColor = '#000000';
    // JointJS invokes initialize() from its base constructor, before normal subclass field
    // initializers run. Keep this declaration type-only so that first load's version is not reset.
    declare private iconLoadVersion: number | undefined;

    defaults() {
        return {
            type: 'IconElement',
            resizable: true,
            minResizeWidth: 16,
            minResizeHeight: 16,
            size: { ...IconElement.DefaultSize },
            attrs: {
                body: {
                    fill: 'transparent',
                    stroke: 'transparent',
                    strokeWidth: 2,
                    width: 'calc(w)',
                    height: 'calc(h)',
                    cursor: 'move',
                },
                icon: {
                    width: 'calc(w)',
                    height: 'calc(h)',
                    viewBox: IconShapeDefaults.viewBox,
                    preserveAspectRatio: 'xMidYMid meet',
                },
                iconPath: {
                    d: IconShapeDefaults.d,
                    fill: IconElement.DefaultColor,
                    stroke: IconElement.DefaultColor,
                    strokeWidth: 1,
                    'pointer-events': 'none',
                },
            },
            markup: [
                {
                    tagName: 'rect',
                    selector: 'body',
                },
                {
                    tagName: 'svg',
                    selector: 'icon',
                    namespace: 'http://www.w3.org/2000/svg',
                    children: [
                        {
                            tagName: 'path',
                            selector: 'iconPath',
                        },
                    ],
                },
            ],
        }
    }

    initialize(...args: any[]): void {
        super.initialize(...args);

        const iconName = this.getIconName();
        const shapeColor = this.getShapeColor();
        this.setIconName(iconName);
        this.setShapeColor(shapeColor);
    }

    public async setIconName(iconName: string) {
        const loadVersion = (this.iconLoadVersion ?? 0) + 1;
        this.iconLoadVersion = loadVersion;
        const shape = await fetchIconShape(iconName);
        // Deserialisation starts an icon load from initialize(). A later explicit patch must win
        // even when the older request happens to finish last.
        if (loadVersion !== this.iconLoadVersion) {
            return;
        }
        this.attr('icon/viewBox', shape.viewBox);
        this.attr('iconPath/d', shape.d);
        this.prop('iconName', iconName);
    }

    public getIconName(): string {
        return (this.prop('iconName') as string) ?? 'Production Facility';
    }

    public setShapeColor(color: string) {
        const value = color || IconElement.DefaultColor;
        this.attr('iconPath/fill', value);
        this.attr('iconPath/stroke', value);
        this.prop('iconShapeColor', value);
    }

    public getShapeColor(): string {
        return (this.prop('iconShapeColor') as string)
            || (this.prop('iconColor') as string)
            || (this.attr('iconPath/fill') as string)
            || IconElement.DefaultColor;
    }
}
Object.assign(joint.shapes, { IconElement });

// Define a custom element to represent a drawn rectangle
export class DrawnRectangle extends joint.shapes.standard.Rectangle {
    defaults() {
        return {
            type: 'DrawnRectangle',
            resizable: true,
            attrs: {
                body: {
                    fill: 'none',
                    stroke: '#000000',
                    strokeWidth: 2,
                    width: 'calc(w)',
                    height: 'calc(h)',
                    cursor: 'move',
                    padding: 10,
                },
            },
            markup: [{
                tagName: 'rect',
                selector: 'body'
            }],
        }
    }

    public setBorderColor(color: string) {
        this.attr('body/stroke', color);
    }

    public getBorderColor(): string {
        return (this.attr('body/stroke') as string) || '#000000';
    }
}


Object.assign(joint.shapes, { DrawnRectangle });

// Define a custom link to represent a drawn line (not connected to elements)
export class DrawnLine extends joint.shapes.standard.Link {
    defaults() {
        return joint.util.defaultsDeep({
            type: 'DrawnLine',
            attrs: {
                line: {
                    stroke: '#000000',
                    strokeWidth: 2,
                    sourceMarker: {
                        type: 'circle',
                        r: 1,
                        stroke: 'none',
                        fill: '#000000',
                    },
                    targetMarker: {
                        type: 'circle',
                        r: 1,
                        stroke: 'none',
                        fill: '#000000',
                    },
                },
            },
            connector: { name: 'straight' },
        }, joint.shapes.standard.Link.prototype.defaults);
    }

    public setStrokeColor(color: string) {
        this.attr('line/stroke', color);
        // also update DrawnLine endcap marker circle colors
        this.attr('line/sourceMarker/fill', color);
        this.attr('line/targetMarker/fill', color);
    }

    public getStrokeColor(): string {
        return (this.attr('line/stroke') as string) || '#000000';
    }

    public setStrokeWidth(width: number) {
        this.attr('line/strokeWidth', width);
        // also update DrawnLine endcap marker circle radius
        this.attr('line/sourceMarker/r', width / 2);
        this.attr('line/targetMarker/r', width / 2);
    }

    public getStrokeWidth(): number {
        return (this.attr('line/strokeWidth') as number) || 2;
    }
}

Object.assign(joint.shapes, { DrawnLine });

// Custom link tool for dragging DrawnLine endpoints (source or target)
// Uses the Control class to create a draggable circle handle
class DrawnLineEndpointTool extends joint.linkTools.Control {
    private endpointType: 'source' | 'target';

    constructor(options: { endpoint: 'source' | 'target' }) {
        super({
            selector: null, // We position manually based on source/target
        });
        this.endpointType = options.endpoint;
    }

    protected getPosition(view: joint.dia.LinkView): joint.dia.Point {
        const link = view.model;
        const point = this.endpointType === 'source' ? link.source() : link.target();
        // Return the point coordinates (for point-based links)
        if (point && typeof (point as any).x === 'number' && typeof (point as any).y === 'number') {
            return { x: (point as any).x, y: (point as any).y };
        }
        // Fallback to link endpoint coordinates
        const endpoint = this.endpointType === 'source' 
            ? view.getEndAnchor('source') 
            : view.getEndAnchor('target');
        return endpoint || { x: 0, y: 0 };
    }

    protected setPosition(view: joint.dia.LinkView, coordinates: joint.dia.Point): void {
        const link = view.model;
        // Snap to grid
        const paper = view.paper;
        const gridSize = paper?.options?.gridSize as number || 15;
        const snappedPoint = {
            x: Math.round(coordinates.x / gridSize) * gridSize,
            y: Math.round(coordinates.y / gridSize) * gridSize,
        };
        
        if (this.endpointType === 'source') {
            link.source(snappedPoint);
        } else {
            link.target(snappedPoint);
        }
    }
}

// Source endpoint tool for DrawnLine
export class DrawnLineSourceTool extends DrawnLineEndpointTool {
    constructor() {
        super({
            endpoint: 'source',
        });
    }
}

// Target endpoint tool for DrawnLine  
export class DrawnLineTargetTool extends DrawnLineEndpointTool {
    constructor() {
        super({
            endpoint: 'target',
        });
    }
}

// Define a custom element to represent a TextBox where the label fills the body area
export class TextBox extends joint.shapes.standard.Rectangle implements IFillColorable, ITextColorable, IBorderColorable {
    defaults() {
        return {
            type: 'TextBox',
            resizable: true,
            attrs: {
                body: {
                    fill: '#FFFFFF',
                    stroke: '#000000',
                    strokeWidth: 2,
                    width: 'calc(w)',
                    height: 'calc(h)',
                    cursor: 'move',
                },
                label: {
                    textAnchor: 'middle',
                    textVerticalAnchor: 'middle',
                    x: 'calc(0.5*w)',
                    y: 'calc(0.5*h)',
                    fill: '#000000',
                    fontSize: 14,
                    fontFamily: '"Open Sans", sans-serif',
                    fontWeight: 'bold',
                    text: '',
                    pointerEvents: 'none',
                    textWrap: {
                        width: 'calc(w)',
                        height: 'calc(h)',
                        ellipsis: true
                    }
                },
            },
            markup: [{
                tagName: 'rect',
                selector: 'body'
            }, {
                tagName: 'text',
                selector: 'label'
            }],
        }
    }

    public setLabel(label: string) {
        this.attr('label/text', label);
    }
    
    public setColor(color: string) {
        this.attr('body/fill', color);
    }

    public getColor(): string {
        return (this.attr('body/fill') as string) || '#FFFFFF';
    }
    
    public setFontColor(color: string) {
        this.attr('label/fill', color);
    }

    public getFontColor(): string {
        return (this.attr('label/fill') as string) || '#000000';
    }

    public setBorderColor(color: string) {
        this.attr('body/stroke', color);
    }

    public getBorderColor(): string {
        return (this.attr('body/stroke') as string) || '#000000';
    }

    public setPadding(value: number) {
        this.attr('body/padding', value);
        this.setLabelPadding(value);
    }

    public setLabelPadding(padding: number) {
        this.attr('label/textWrap', {
            width: `calc(w) - ${padding * 2}`,
            height: `calc(h) - ${padding * 2}`,
            ellipsis: true
        });
    }
}

export class Callout extends joint.shapes.standard.Rectangle implements IFillColorable, ITextColorable {
    private static readonly SEGMENT_INSET_RATIO = 0.18;
    private static readonly SEGMENT_EXTENT_RATIO = 0.4;
    private static readonly DEFAULT_TIP_OFFSET_RATIO = 0.4;
    private static readonly DEFAULT_TIP_OFFSET_MIN = 30;
    // Tail base always spans the full attachment segment
    // Guard to avoid recursion when adjusting tail tip during position changes
    private _adjustingTailTip: boolean = false;
    private _syncingTailAppearance: boolean = false;

    defaults() {
        return {
            type: 'Callout',
            resizable: true,
            attrs: {
                tail: {
                    fill: '#CECECE',
                    stroke: '#CECECE',
                    strokeWidth: 2,
                    cursor: 'move',
                    pointerEvents: 'auto',
                    d: 'M 0 0 L 0 0 L 0 0 Z',
                    'data-tail-body': 'true',
                },
                body: {
                    fill: '#CECECE',
                    stroke: '#CECECE',
                    strokeWidth: 2,
                    rx: '.5rem',
                    ry: '.5rem',
                    width: 'calc(w)',
                    height: 'calc(h)',
                    cursor: 'move',
                },
                label: {
                    textAnchor: 'middle',
                    textVerticalAnchor: 'middle',
                    x: 'calc(0.5*w)',
                    y: 'calc(0.5*h)',
                    fill: '#000000',
                    fontSize: 14,
                    fontFamily: '"Open Sans", sans-serif',
                    fontWeight: 'bold',
                    text: '',
                    pointerEvents: 'none',
                    textWrap: {
                        width: 'calc(w)',
                        height: 'calc(h)',
                        ellipsis: true
                    }
                },
                tailHandle: {
                    r: 6,
                    fill: '#FFFFFF',
                    stroke: '#00a6ff',
                    strokeWidth: 1,
                    cursor: 'grab',
                    'data-tail-handle': 'true',
                    pointerEvents: 'none',
                    event: 'callout:tail:pointerdown',
                    display: 'none',
                    cx: 0,
                    cy: 0,
                },
            },
            markup: [
                { tagName: 'path', selector: 'tail' },
                { tagName: 'rect', selector: 'body' },
                { tagName: 'text', selector: 'label' },
                { tagName: 'circle', selector: 'tailHandle' },
            ],
        };
    }

    initialize(...args: any[]): void {
        super.initialize(...args);

        this.on('change:size', this.updateTailGeometry, this);
        this.on('change:attrs', this.syncTailAppearance, this);
        this.on('change:tailTip', this.updateTailGeometry, this);
        this.on('change:position', this.onPositionChanged, this);
        this.on('remove', () => this.hideTailHandle());

        if (this.prop('tailId')) {
            this.prop('tailId', null);
        }

        this.ensureTailTip();
        this.updateTailGeometry();
        this.syncTailAppearance();
    }

    // Preserve the absolute paper location of the tail tip across position changes
    private onPositionChanged(): void {
        if (this._adjustingTailTip) {
            return;
        }

        // Skip tail tip adjustment when embedded (e.g. in GroupSelection being dragged),
        // but NOT when the element is being resized - resize should preserve tail tip position
        if (this.isEmbedded() && !this.prop('isResizing')) {
            return;
        }

        const prevPos = (this as any).previous?.('position') as { x: number; y: number } | undefined;
        const currPos = this.position();

        if (!prevPos) {
            this.updateTailGeometry();
            return;
        }

        const suppress = !!this.prop('suppressTailTipLock');

        if (!suppress) {
            const tip = this.getTailTip();
            const dx = prevPos.x - currPos.x;
            const dy = prevPos.y - currPos.y;

            this._adjustingTailTip = true;
            this.prop('tailTip', { x: tip.x + dx, y: tip.y + dy });
            this._adjustingTailTip = false;
        }

        this.updateTailGeometry();
    }

    public showTailHandle(): void {
        this.attr({ tailHandle: { display: 'inline', pointerEvents: 'visiblePainted' } });
    }

    public hideTailHandle(): void {
        this.attr({ tailHandle: { display: 'none', pointerEvents: 'none' } });
    }

    public setTailHandleCursor(cursor: string): void {
        this.attr('tailHandle/cursor', cursor);
    }

    public setTailTip(point: CalloutPoint): void {
        const sanitized = {
            x: Number.isFinite(point.x) ? point.x : 0,
            y: Number.isFinite(point.y) ? point.y : 0,
        };

        this.prop('tailTip', sanitized);
        this.updateTailGeometry();
    }

    public getTailTip(): CalloutPoint {
        return this.ensureTailTip();
    }

    private ensureTailTip(): CalloutPoint {
        const size = this.size();
        const width = Number(size.width) || 0;
        const height = Number(size.height) || 0;

        let tailTip = this.prop('tailTip') as CalloutPoint | null;
        if (!tailTip || !Number.isFinite(tailTip.x) || !Number.isFinite(tailTip.y)) {
            const offset = Math.max(Callout.DEFAULT_TIP_OFFSET_MIN, height * Callout.DEFAULT_TIP_OFFSET_RATIO);
            tailTip = {
                x: width * .18,
                y: height + offset,
            };
            this.prop('tailTip', tailTip);
        }

        return tailTip;
    }

    private updateTailGeometry(): void {
        const size = this.size();
        const width = Number(size.width) || 0;
        const height = Number(size.height) || 0;

        if (width <= 0 || height <= 0) {
            return;
        }

        const tailTip = this.ensureTailTip();
        const segments = this.computeSegments(width, height);
        if (!segments.length) {
            return;
        }

        let bestSegmentIndex = 0;
        let bestProjection = this.projectPointToSegment(tailTip, segments[0]);

        for (let i = 1; i < segments.length; i++) {
            const projection = this.projectPointToSegment(tailTip, segments[i]);
            if (projection.distance < bestProjection.distance) {
                bestProjection = projection;
                bestSegmentIndex = i;
            }
        }

        const segment = segments[bestSegmentIndex];
        const base = this.computeTailBase(segment, bestProjection.t);

        const path = `M ${base.end.x} ${base.end.y} L ${tailTip.x} ${tailTip.y} L ${base.start.x} ${base.start.y} Z`;

        this.attr({
            tail: { d: path },
            tailHandle: { cx: tailTip.x, cy: tailTip.y },
        });

        this.prop('tailSegmentIndex', bestSegmentIndex);
        this.prop('tailAnchor', base.anchor);

        this.syncTailAppearance();
    }

    private computeSegments(width: number, height: number): CalloutSegment[] {
        const insetX = width * Callout.SEGMENT_INSET_RATIO;
        const outerX = width * (1 - Callout.SEGMENT_INSET_RATIO);
        const nearX = width * Callout.SEGMENT_EXTENT_RATIO;
        const farX = width * (1 - Callout.SEGMENT_EXTENT_RATIO);

        const insetY = height * Callout.SEGMENT_INSET_RATIO;
        const outerY = height * (1 - Callout.SEGMENT_INSET_RATIO);
        const nearY = height * Callout.SEGMENT_EXTENT_RATIO;
        const farY = height * (1 - Callout.SEGMENT_EXTENT_RATIO);

        return [
            this.createSegment({ x: insetX, y: 0 }, { x: nearX, y: 0 }),
            this.createSegment({ x: farX, y: 0 }, { x: outerX, y: 0 }),
            this.createSegment({ x: width, y: insetY }, { x: width, y: nearY }),
            this.createSegment({ x: width, y: farY }, { x: width, y: outerY }),
            this.createSegment({ x: farX, y: height }, { x: outerX, y: height }),
            this.createSegment({ x: insetX, y: height }, { x: nearX, y: height }),
            this.createSegment({ x: 0, y: farY }, { x: 0, y: outerY }),
            this.createSegment({ x: 0, y: insetY }, { x: 0, y: nearY }),
        ];
    }

    private createSegment(start: CalloutPoint, end: CalloutPoint): CalloutSegment {
        const delta = { x: end.x - start.x, y: end.y - start.y };
        const length = Math.hypot(delta.x, delta.y);
        return { start, end, delta, length };
    }

    private projectPointToSegment(point: CalloutPoint, segment: CalloutSegment) {
        if (segment.length === 0) {
            const distance = Math.hypot(point.x - segment.start.x, point.y - segment.start.y);
            return { point: segment.start, distance, t: 0 };
        }

        const lengthSquared = segment.delta.x * segment.delta.x + segment.delta.y * segment.delta.y;
        const toPoint = { x: point.x - segment.start.x, y: point.y - segment.start.y };
        const rawT = (toPoint.x * segment.delta.x + toPoint.y * segment.delta.y) / lengthSquared;
        const t = Math.max(0, Math.min(1, rawT));
        const closest = this.pointAlongSegment(segment, t);
        const distance = Math.hypot(point.x - closest.x, point.y - closest.y);
        return { point: closest, distance, t };
    }

    private pointAlongSegment(segment: CalloutSegment, t: number): CalloutPoint {
        return {
            x: segment.start.x + segment.delta.x * t,
            y: segment.start.y + segment.delta.y * t,
        };
    }

    private computeTailBase(segment: CalloutSegment, t: number) {
        // Span the entire segment to create a seamless connection to the rectangle edge
        const start = segment.start;
        const end = segment.end;
        const anchor = this.pointAlongSegment(segment, Math.max(0, Math.min(1, t)));
        return { start, end, anchor };
    }

    private syncTailAppearance(): void {
        if (this._syncingTailAppearance) {
            return;
        }

        this._syncingTailAppearance = true;
        try {
            const fill = this.attr('body/fill') ?? '#FFFFFF';
            const stroke = this.attr('body/stroke') ?? 'transparent';
            const strokeWidth = this.attr('body/strokeWidth') ?? 3;

            this.attr({
                tail: {
                    fill,
                    stroke,
                    strokeWidth,
                },
            });
        } finally {
            this._syncingTailAppearance = false;
        }
    }

    // Return bounds for the entire callout, including the tail and drag handle.
    public getFullBBox(): Rect {
        const baseBBox = super.getBBox();
        const size = this.size();
        const width = Number(size.width) || 0;
        const height = Number(size.height) || 0;
        const tailHandleRadius = this.attr('tailHandle/r') || 0 as number;

        let minLocalX = 0;
        let maxLocalX = width;
        let minLocalY = 0;
        let maxLocalY = height;

        const tailTip = this.ensureTailTip();
        if (Number.isFinite(tailTip.x) && Number.isFinite(tailTip.y)) {
            minLocalX = Math.min(minLocalX, tailTip.x - tailHandleRadius);
            maxLocalX = Math.max(maxLocalX, tailTip.x + tailHandleRadius);
            minLocalY = Math.min(minLocalY, tailTip.y - tailHandleRadius);
            maxLocalY = Math.max(maxLocalY, tailTip.y + tailHandleRadius);
        }

        const bboxWidth = Math.max(0, maxLocalX - minLocalX);
        const bboxHeight = Math.max(0, maxLocalY - minLocalY);

        return {
            x: baseBBox.x + minLocalX,
            y: baseBBox.y + minLocalY,
            width: bboxWidth,
            height: bboxHeight,
        };
    }
    
    public setLabel(label: string) {
        this.attr('label/text', label);
    }
    
    public getColor() {
        return (this.attr('body/fill') as string) || '#CECECE';
    }

    public setColor(color: string): void {
        this.attr('body/fill', color);
        this.attr('body/stroke', color);
        this.syncTailAppearance();
    }
    
    public getFontColor() {
        return (this.attr('label/fill') as string) || '#000000';
    }

    public setFontColor(color: string): void {
        // Change the label text color for callouts
        this.attr('label/fill', color);
    }
}

Object.assign(joint.shapes, { TextBox, Callout });

export class GroupSelection extends joint.shapes.standard.Rectangle {
    static readonly Padding = 2;
    static readonly StrokeWidth = 2;
    
    defaults() {
        return {
            type: 'GroupSelection',
            resizable: false,
            size: { width: 1, height: 1 },
            attrs: {
                body: {
                    fill: 'transparent',
                    stroke: '#00a6ff',
                    strokeWidth: GroupSelection.StrokeWidth,
                    cursor: 'move',
                    // Adjust position and size to account for stroke width and padding
                    x: `calc(x-${GroupSelection.StrokeWidth + GroupSelection.Padding})`,
                    y: `calc(y-${GroupSelection.StrokeWidth + GroupSelection.Padding})`,
                    width: 'calc(w+4)',
                    height: 'calc(h+4)',
                },
            },
            markup: [{
                tagName: 'rect',
                selector: 'body'
            }],
        }
    }
}

Object.assign(joint.shapes, { GroupSelection });
